import { useEffect } from "react";
import {
  isCheckedIn,
  isWfhAutoCheckoutActive,
  beaconCheckout,
  resumeWfhTracking,
} from "../utils/wfhTracking";

// Confirmation message shown when the user tries to close/refresh while checked
// in. NOTE: modern browsers ignore custom text and show their own generic
// "Leave site?" dialog for security reasons — the string is still provided for
// the few engines that honour it. The important behaviour (auto-checkout when
// the user confirms leaving) is handled in the pagehide handler below.
export const CONFIRM_MESSAGE =
  "You are currently checked in from WFH. Closing this tab will automatically check you out from the attendance system. Do you want to continue?";

// App-wide guard: while the user is checked in, closing the tab / window or
// refreshing prompts a confirmation. If the user confirms (the page actually
// unloads) we fire an auto-checkout beacon. If they cancel, nothing happens and
// tracking continues.
export const useCheckoutOnTabClose = () => {
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isCheckedIn() || !isWfhAutoCheckoutActive()) return;
      // Triggers the native confirmation dialog.
      event.preventDefault();
      event.returnValue = CONFIRM_MESSAGE;
      return CONFIRM_MESSAGE;
    };

    // pagehide fires when the page is actually being torn down (i.e. the user
    // confirmed leaving). This is where we perform the auto-checkout.
    const handlePageHide = (event) => {
      // event.persisted === true means the page is going into the bfcache
      // (e.g. back/forward navigation), not actually closing — don't check out.
      if (event.persisted) return;
      if (!isCheckedIn() || !isWfhAutoCheckoutActive()) return;
      beaconCheckout();
    };

    // Resume location capture if we reloaded into an active WFH session.
    resumeWfhTracking();

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);
};

// Zero-render component so the guard can be mounted at the app root.
const CheckoutOnTabCloseGuard = () => {
  useCheckoutOnTabClose();
  return null;
};

export default CheckoutOnTabCloseGuard;
